import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";


/* ───────────────── TYPES ───────────────── */

export type ProductListing = {
  listingId:  string;
  merchantId: string;
  hostId:     string;

  name?:    string;
  barcode?: string;
  qrCode?:  string;
  sku?:     string;

  productId?: number | null;

  rate?:      number;
  costPrice?: number;

  stock?:    number;
  minStock?: number;
  unit?:     string;
  weight?:   number;

  category?:    string;
  brand?:       string;
  description?: string;
  vatRate?:     string;

  available?: boolean;

  photos?:   string[];
  videoUrl?: string;

  isPublished: boolean;
  isFlagged:   boolean;
  isComplete:  boolean;
  isReviewed:  boolean;
  isDraft?:    boolean;

  joinDate:   string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type ProductsState = {
  listings:             ProductListing[];
  filteredListings:     ProductListing[];
  filters: {
    city:    string | null;
    country: string | null;
  };
  currentListing:       ProductListing | null;
  unsubscribe?:         () => void;
  loading:              boolean;
  error:                string | null;
  merchantListings:        ProductListing[];
  merchantListingsId:      string | null;
  merchantListingsLoading: boolean;
  merchantUnsubscribe?:    () => void;
};

const initialState: ProductsState = {
  listings:                [],
  currentListing:          null,
  unsubscribe:             undefined,
  loading:                 false,
  error:                   null,
  filteredListings:        [],
  filters:                 { city: null, country: null },
  merchantListings:        [],
  merchantListingsId:      null,
  merchantListingsLoading: false,
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

export const parseProduct = (
  id: string,
  raw: Record<string, any>
): ProductListing => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    listingId:  id,
    merchantId: raw.merchantId ?? "",
    hostId:     raw.hostId     ?? "",

    name:      raw.name     ?? "",
    barcode:   raw.barcode  ?? "",
    qrCode:    raw.qrCode   ?? "",
    sku:       raw.sku      ?? "",

    productId: raw.productId ?? null,

    rate:      raw.rate      ?? 0,
    costPrice: raw.costPrice ?? 0,

    stock:     raw.stock    ?? 0,
    minStock:  raw.minStock ?? 0,
    unit:      raw.unit     ?? "pcs",
    weight:    raw.weight   ?? 0,

    category:    raw.category    ?? "",
    brand:       raw.brand       ?? "",
    description: raw.description ?? "",
    vatRate:     raw.vatRate     ?? "20%",

    available: raw.available ?? true,

    photos:   raw.photos   ?? [],
    videoUrl: raw.videoUrl ?? "",

    isPublished: raw.isPublished ?? false,
    isFlagged:   raw.isFlagged   ?? false,
    isComplete:  raw.isComplete  ?? false,
    isReviewed:  raw.isReviewed  ?? false,
    isDraft:     raw.isDraft     ?? true,

    joinDate: raw.joinDate ?? new Date().toISOString(),

    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),

    ...safeRaw,
  };
};

/* ───────────────── UPSERT ─────────────────

export const upsertProduct = createAsyncThunk<
  ProductListing,
  {
    listingId?: string;
    data: Omit<
      Partial<ProductListing>,
      | "merchantId"
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "photos"
      | "videoUrl"
    >;
  }
>("products/upsert", async ({ listingId, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
        console.log("[upsertProduct] currentUser:", user?.uid ?? "NULL");

    if (!user) throw new Error("User not authenticated");

    const merchantQuery = query(
      collection(db, "merchants"),
      where("userId", "==", user.uid)
    );
    const merchantSnap = await getDocs(merchantQuery);
    if (merchantSnap.empty) throw new Error("No merchant found");
    const merchantId = merchantSnap.docs[0].id;
    console.log("[upsertProduct] merchantId:", merchantId);

    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );

    const baseData = {
      ...cleanData,
      merchantId,
      hostId:    user.uid,
      updatedAt: serverTimestamp(),
    };

    if (listingId) {
      const refDoc = doc(db, "products", listingId);
      await updateDoc(refDoc, baseData);
      const snap = await getDoc(refDoc);
      return parseProduct(snap.id, snap.data() || {});
    }

    const payload = {
      ...baseData,
      joinDate:    new Date().toISOString(),
      createdAt:   serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft:     cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged:   false,
      isComplete:  false,
      isReviewed:  false,
    };

    const colRef = collection(db, "products");
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });

    const snap = await getDoc(newDoc);
    return parseProduct(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});
*/
export const upsertProduct = createAsyncThunk<
  ProductListing,
  {
    listingId?: string;
    data: Omit<
      Partial<ProductListing>,
      | "merchantId"
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "photos"
      | "videoUrl"
    >;
  }
>(
  "products/upsert",
  async ({ listingId, data }, { rejectWithValue }) => {
    try {
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("[upsertProduct] START");
      console.log("[upsertProduct] listingId:", listingId);
      console.log("[upsertProduct] raw data:", data);

      const user = getAuth().currentUser;
      console.log("[upsertProduct] currentUser:", user?.uid ?? "NULL");

      if (!user) {
        console.error("[upsertProduct] ERROR: No user logged in");
        throw new Error("User not authenticated");
      }

      const merchantQuery = query(
        collection(db, "merchants"),
        where("userId", "==", user.uid)
      );

      console.log("[upsertProduct] fetching merchant...");
      const merchantSnap = await getDocs(merchantQuery);

      console.log(
        "[upsertProduct] merchant docs found:",
        merchantSnap.docs.length
      );

      if (merchantSnap.empty) {
        console.error("[upsertProduct] ERROR: No merchant found");
        throw new Error("No merchant found");
      }

      const merchantId = merchantSnap.docs[0].id;
      console.log("[upsertProduct] merchantId:", merchantId);

      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      );

      console.log("[upsertProduct] cleanData:", cleanData);

      const baseData = {
        ...cleanData,
        merchantId,
        hostId: user.uid,
        updatedAt: serverTimestamp(),
      };

      console.log("[upsertProduct] baseData ready:", baseData);

      const refDoc = listingId
        ? doc(db, "products", listingId)
        : doc(collection(db, "products"));

      console.log("[upsertProduct] doc path:", refDoc.path);

      const payload = {
        ...baseData,
        joinDate: new Date().toISOString(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPublished: cleanData.isPublished ?? false,
        isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
        isFlagged: false,
        isComplete: false,
        isReviewed: false,
      };

      console.log("[upsertProduct] FINAL payload:", payload);

      console.log("[upsertProduct] writing to Firestore...");

      await setDoc(refDoc, payload, { merge: true });

      console.log("[upsertProduct] write SUCCESS");

      console.log("[upsertProduct] reading back doc...");
      const snap = await getDoc(refDoc);

      if (!snap.exists()) {
        console.error("[upsertProduct] ERROR: doc does not exist after write");
        throw new Error("Write succeeded but document missing");
      }

      console.log("[upsertProduct] snap data:", snap.data());

      const result = parseProduct(refDoc.id, snap.data() || {});

      console.log("[upsertProduct] RETURNING:", result);
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

      return result;
    } catch (err: any) {
      console.error("[upsertProduct] FAILED:", err);
      return rejectWithValue(err.message || "upsert failed");
    }
  }
);


/* ───────────────── LISTENERS ───────────────── */

export const listenToMerchantProduct =
  ({ merchantId }: { merchantId: string }) =>
  (dispatch: AppDispatch) => {
    if (!merchantId) return;
    const q = query(
      collection(db, "products"),
      where("merchantId",  "==", merchantId),
      where("isPublished", "==", true)
    );
    return onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseProduct(d.id, d.data()))));
    });
  };

export const listenToHostProduct =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;
    const { unsubscribe } = getState().products;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const q = query(collection(db, "products"), where("hostId", "==", hostId));
    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseProduct(d.id, d.data()))));
    });
    dispatch(setUnsubscribe(unsub));
  };


  export const listenToPublishedProductsByMerchant =
  (merchantId: string) =>
  (dispatch: AppDispatch) => {

    if (!merchantId) return;

    const q = query(
      collection(db, "products"),
      where("merchantId", "==", merchantId),
      where("isPublished", "==", true),
      where("available", "==", true)
    );

    return onSnapshot(q, (snap) => {
  const products: ProductListing[] = snap.docs.map((doc) =>
  parseProduct(doc.id, doc.data())
);

dispatch(setListings(products));
    });
  };

export const listenToPublishedProducts =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().products;
    if (unsubscribe) return;

    const merchantsQuery = query(
      collection(db, "merchants"),
      where("isPublished", "==", true)
    );

    const merchantsUnsub = onSnapshot(merchantsQuery, (merchantSnap) => {
      const publishedMerchantIds = merchantSnap.docs.map((d) => d.id);
      if (!publishedMerchantIds.length) { dispatch(setListings([])); return; }

      const chunkSize = 10;
      const chunks: string[][] = [];
      for (let i = 0; i < publishedMerchantIds.length; i += chunkSize) {
        chunks.push(publishedMerchantIds.slice(i, i + chunkSize));
      }

      let allProducts: ProductListing[] = [];
      const listeners: (() => void)[] = [];

      chunks.forEach((chunk) => {
        const productsQuery = query(
          collection(db, "products"),
          where("isPublished", "==", true),
          where("merchantId", "in", chunk)
        );
        const unsubProducts = onSnapshot(productsQuery, (snap) => {
          const products = snap.docs.map((d) => parseProduct(d.id, d.data()));
          allProducts = [
            ...allProducts.filter(
              (p) => !products.some((np) => np.listingId === p.listingId)
            ),
            ...products,
          ];
          dispatch(setListings(allProducts));
        });
        listeners.push(unsubProducts);
      });

      dispatch(setUnsubscribe(() => {
        merchantsUnsub();
        listeners.forEach((u) => u());
      }));
    });
  };

export const listenToProductData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;
    const { unsubscribe } = getState().products;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const refDoc = doc(db, "products", listingId);
    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) { dispatch(setCurrentListing(null)); return; }
      dispatch(setCurrentListing(parseProduct(snap.id, snap.data())));
    });
    dispatch(setUnsubscribe(unsub));
  };

/* ─────────────────────────────────────────────────────────
   listenToMerchantsAndProducts
   Stage 1 — listens to all published merchants
   Stage 2 — listens to all products of those merchants
             → dispatches to state.products.listings
   Used by Scanner so barcode lookup is live in Redux.
   Returns a combined cleanup function.
───────────────────────────────────────────────────────── */

let _merchantsAndProductsMerchantsUnsub: (() => void) | null = null;
let _merchantsAndProductsProductsUnsub:  (() => void) | null = null;

export const listenToMerchantsAndProducts =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {

    if (_merchantsAndProductsMerchantsUnsub) { _merchantsAndProductsMerchantsUnsub(); _merchantsAndProductsMerchantsUnsub = null; }
    if (_merchantsAndProductsProductsUnsub)  { _merchantsAndProductsProductsUnsub();  _merchantsAndProductsProductsUnsub  = null; }

    const merchantsQuery = query(
      collection(db, "merchants"),
      where("isPublished", "==", true)
    );

    _merchantsAndProductsMerchantsUnsub = onSnapshot(
      merchantsQuery,
      (merchantSnap) => {
        const merchantIds = merchantSnap.docs.map((d) => d.id);

        if (!merchantIds.length) {
          dispatch(setListings([]));
          return;
        }

        if (_merchantsAndProductsProductsUnsub) {
          _merchantsAndProductsProductsUnsub();
          _merchantsAndProductsProductsUnsub = null;
        }

        const chunkSize = 10;
        const chunks: string[][] = [];
        for (let i = 0; i < merchantIds.length; i += chunkSize) {
          chunks.push(merchantIds.slice(i, i + chunkSize));
        }

        let allProducts: ProductListing[] = [];
        const chunkUnsubs: (() => void)[] = [];

        chunks.forEach((chunk) => {
          const productsQuery = query(
            collection(db, "products"),
            where("isPublished", "==", true),
            where("available",   "==", true),
            where("merchantId",  "in", chunk)
          );

          const unsub = onSnapshot(
            productsQuery,
            (snap) => {
              const incoming = snap.docs.map((d) => parseProduct(d.id, d.data()));
              allProducts = [
                ...allProducts.filter(
                  (p) => !incoming.some((np) => np.listingId === p.listingId)
                ),
                ...incoming,
              ];
              dispatch(setListings(allProducts));
            },
            (err) => dispatch(setError(err.message))
          );

          chunkUnsubs.push(unsub);
        });

        _merchantsAndProductsProductsUnsub = () => chunkUnsubs.forEach((u) => u());
      },
      (err) => dispatch(setError(err.message))
    );

    return () => {
      if (_merchantsAndProductsMerchantsUnsub) { _merchantsAndProductsMerchantsUnsub(); _merchantsAndProductsMerchantsUnsub = null; }
      if (_merchantsAndProductsProductsUnsub)  { _merchantsAndProductsProductsUnsub();  _merchantsAndProductsProductsUnsub  = null; }
    };
  };

export const stopMerchantsAndProductsListener = () => {
  if (_merchantsAndProductsMerchantsUnsub) { _merchantsAndProductsMerchantsUnsub(); _merchantsAndProductsMerchantsUnsub = null; }
  if (_merchantsAndProductsProductsUnsub)  { _merchantsAndProductsProductsUnsub();  _merchantsAndProductsProductsUnsub  = null; }
};

/* ───────────────── MEDIA ───────────────── */

export const upsertProductMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; files: File[]; type: "photo" | "video" }
>(
  "products/upsertMedia",
  async ({ listingId, files, type }, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    let id = listingId;
    const uploaded: string[] = [];

    for (const file of files) {
      const folder = type === "photo" ? "photos" : "videos";
      const path   = `products/${id || "new"}/${folder}/${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      uploaded.push(await getDownloadURL(storageRef));
    }

    if (!id) {
      const created = await dispatch(
        upsertProduct({
          data: {
            photos:   type === "photo" ? uploaded : [],
            videoUrl: type === "video" ? uploaded[0] : "",
          },
        })
      ).unwrap();
      id = created.listingId;
    } else {
      await dispatch(
        upsertProduct({
          listingId: id,
          data: type === "photo"
            ? { photos: uploaded }
            : { videoUrl: uploaded[0] },
        })
      );
    }

    return { listingId: id!, downloadURLs: uploaded };
  }
);

export const deleteProductPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>(
  "products/deletePhoto",
  async ({ listingId, photoUrl }) => {
    const refDoc = doc(db, "products", listingId);
    const snap   = await getDoc(refDoc);
    if (!snap.exists()) throw new Error("Listing not found");

    const photos = snap.data()?.photos || [];
    await updateDoc(refDoc, {
      photos: photos.filter((p: string) => p !== photoUrl),
    });

    if (photoUrl.includes("/o/")) {
      const encoded = photoUrl.split("/o/")[1].split("?")[0];
      await deleteObject(ref(storage, decodeURIComponent(encoded)));
    }
    return photoUrl;
  }
);

/* ───────────────── DELETE ───────────────── */

export const deleteProduct = createAsyncThunk<{ listingId: string }, string>(
  "products/delete",
  async (listingId) => {
    await deleteDoc(doc(db, "products", listingId));
    return { listingId };
  }
);

/* ───────────────── CATEGORY LISTENERS ───────────────── */

export const listenToProductsByCategory =
  (categoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;
    const { unsubscribe } = getState().products;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const q = query(
      collection(db, "products"),
      where("isPublished", "==", true),
      where("categoryId",  "==", categoryId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const products = snap.docs.map((d) => parseProduct(d.id, d.data()));
        if (products.length === 0) {
          dispatch(listenToPublishedProducts());
          return;
        }
        dispatch(setListings(products));
      },
      (error) => dispatch(setError(error.message))
    );
    dispatch(setUnsubscribe(unsub));
  };

export const listenToCategoryGroup =
  (categoryIds: number[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryIds.length) return;
    const { unsubscribe } = getState().products;
    if (unsubscribe) { unsubscribe(); dispatch(setUnsubscribe(undefined)); }

    const q = query(
      collection(db, "products"),
      where("isPublished", "==", true),
      where("categoryId",  "in", categoryIds)
    );
    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseProduct(d.id, d.data()))));
    });
    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── LOCATION FILTERS ───────────────── */

export const filterProductsByMerchantLocation =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().products;
    const { partners }          = getState().commercePartners;

    if (!filters.city && !filters.country) {
      dispatch(setFilteredListings(listings));
      return;
    }

    const filtered = listings.filter((product) => {
      const merchant = partners.find((p: any) => p.merchantId === product.merchantId);
      if (!merchant) return false;
      const cityMatch    = filters.city
        ? merchant.address?.city?.toLowerCase().includes(filters.city.toLowerCase())
        : true;
      const countryMatch = filters.country
        ? merchant.address?.country?.toLowerCase().includes(filters.country.toLowerCase())
        : true;
      return cityMatch && countryMatch;
    });
    dispatch(setFilteredListings(filtered));
  };

export const filterLocalProductsByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!country) {
      dispatch(setFilteredListings(getState().products.listings));
      return;
    }
    try {
      const merchantsQuery = query(
        collection(db, "merchants"),
        where("address.country", "==", country)
      );
      const merchantsSnap      = await getDocs(merchantsQuery);
      const localMerchantIds   = merchantsSnap.docs.map((doc) => doc.id);

      if (!localMerchantIds.length) { dispatch(setFilteredListings([])); return; }

      const filtered = getState().products.listings.filter((product) =>
        localMerchantIds.includes(product.merchantId)
      );
      dispatch(setFilteredListings(filtered));
    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };

/* ───────────────── MERCHANT PRODUCTS (scoped) ───────────────── */

export const listenToMerchantProducts =
  (merchantId: string) =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {
    if (!merchantId) return () => {};

    dispatch(setMerchantListingsLoading(true));
    dispatch(setMerchantListingsId(merchantId));

    const q = query(
      collection(db, "products"),
      where("merchantId",  "==", merchantId),
      where("isPublished", "==", true)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const products: ProductListing[] = snap.docs.map((d) =>
          parseProduct(d.id, d.data())
        );
        dispatch(setMerchantListings(products));
        dispatch(setMerchantListingsLoading(false));
      },
      (err) => {
        dispatch(setError(err.message));
        dispatch(setMerchantListingsLoading(false));
      }
    );

    dispatch(setMerchantUnsubscribe(unsub));
    return unsub;
  };

export const stopMerchantProductsListener =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { merchantUnsubscribe } = getState().products;
    if (merchantUnsubscribe) {
      merchantUnsubscribe();
      dispatch(setMerchantUnsubscribe(undefined));
      dispatch(setMerchantListingsId(null));
      dispatch(clearMerchantListings());
    }
  };

/* ───────────────── SLICE ───────────────── */

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<ProductListing[]>) => {
      state.listings = action.payload;
    },
    setCurrentListing: (state, action: PayloadAction<ProductListing | null>) => {
      state.currentListing = action.payload;
    },
    setFilteredListings: (state, action: PayloadAction<ProductListing[]>) => {
      state.filteredListings = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<{ city?: string | null; country?: string | null }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetProductFilters: (state) => {
      state.filters          = { city: null, country: null };
      state.filteredListings = state.listings;
    },
    setMerchantListings: (state, a: PayloadAction<ProductListing[]>) => {
      state.merchantListings = a.payload;
    },
    clearMerchantListings: (state) => {
      state.merchantListings = [];
    },
    setMerchantListingsId: (state, a: PayloadAction<string | null>) => {
      state.merchantListingsId = a.payload;
    },
    setMerchantListingsLoading: (state, a: PayloadAction<boolean>) => {
      state.merchantListingsLoading = a.payload;
    },
    setMerchantUnsubscribe: (
      state,
      a: PayloadAction<(() => void) | undefined>
    ) => {
      state.merchantUnsubscribe = a.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertProduct.fulfilled, (state, action) => {
        const idx = state.listings.findIndex(
          (l) => l.listingId === action.payload.listingId
        );
        if (idx !== -1) state.listings[idx] = action.payload;
        else state.listings.push(action.payload);
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.listings = state.listings.filter(
          (l) => l.listingId !== action.payload.listingId
        );
      });
  },
});

export const {
  setListings,
  setCurrentListing,
  setUnsubscribe,
  setError,
  setFilters,
  setFilteredListings,
  setMerchantListings,
  clearMerchantListings,
  setMerchantListingsId,
  setMerchantListingsLoading,
  setMerchantUnsubscribe,
} = productsSlice.actions;

export default productsSlice.reducer;